import { formatOsmCityName } from "./reverse-geocode";

export interface CountryCity {
    id: string;
    name: string;
    lat: number;
    lon: number;
}

const PLACE_TYPES = new Set([
    "city",
    "town",
    "village",
    "municipality",
    "hamlet",
    "administrative",
]);

type NominatimSearchResult = {
    place_id?: number | string;
    lat?: string;
    lon?: string;
    name?: string;
    type?: string;
    class?: string;
    display_name?: string;
    address?: {
        suburb?: string;
        neighbourhood?: string;
        quarter?: string;
        hamlet?: string;
        city?: string;
        town?: string;
        village?: string;
        municipality?: string;
    };
};

function isValidCoordinate(lat: number, lon: number): boolean {
    return (
        Number.isFinite(lat) &&
        Number.isFinite(lon) &&
        lat >= -90 &&
        lat <= 90 &&
        lon >= -180 &&
        lon <= 180
    );
}

export function parseNominatimCityResults(results: unknown): CountryCity[] {
    if (!Array.isArray(results)) return [];

    const seen = new Set<string>();
    const cities: CountryCity[] = [];

    for (const item of results) {
        if (!item || typeof item !== "object") continue;
        const result = item as NominatimSearchResult;

        if (result.class === "place" && result.type && !PLACE_TYPES.has(result.type)) {
            continue;
        }

        const lat = result.lat ? Number(result.lat) : NaN;
        const lon = result.lon ? Number(result.lon) : NaN;
        if (!isValidCoordinate(lat, lon)) continue;

        const name =
            result.name?.trim() ||
            formatOsmCityName(result.address, result.display_name) ||
            null;
        if (!name) continue;

        const key = name.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);

        cities.push({
            id: String(result.place_id ?? `${lat},${lon}`),
            name,
            lat: parseFloat(lat.toFixed(3)),
            lon: parseFloat(lon.toFixed(3)),
        });
    }

    return cities.sort((a, b) => a.name.localeCompare(b.name));
}

export function filterCountryCities(cities: CountryCity[], query: string): CountryCity[] {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return cities;
    return cities.filter((city) => city.name.toLowerCase().includes(normalized));
}

/**
 * Search cities in a country using OpenStreetMap Nominatim directly from the
 * browser (CORS-enabled, same as reverseGeocode in reverse-geocode.ts).
 */
export async function fetchCountryCities(
    countryCode: string,
    query?: string,
): Promise<CountryCity[]> {
    const normalizedCountry = countryCode?.trim().toLowerCase();
    if (!normalizedCountry || !/^[a-z]{2}$/.test(normalizedCountry)) return [];

    const normalizedQuery = query?.trim();
    if (!normalizedQuery || normalizedQuery.length < 2) return [];

    try {
        const params = new URLSearchParams({
            format: "json",
            addressdetails: "1",
            limit: "25",
            countrycodes: normalizedCountry,
            q: normalizedQuery,
        });

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 12000);

        const response = await fetch(
            `https://nominatim.openstreetmap.org/search?${params.toString()}`,
            {
                signal: controller.signal,
                headers: { Accept: "application/json" },
            },
        );

        clearTimeout(timeoutId);

        if (!response.ok) {
            return [];
        }

        const data: unknown = await response.json();
        return parseNominatimCityResults(data);
    } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
            console.warn("Country cities request timed out");
        } else {
            console.error("Error fetching country cities:", error);
        }
        return [];
    }
}
